import sanitizeHtml from 'sanitize-html';

interface BlogContentProps {
  content: string;
}

export default function BlogContent({ content }: BlogContentProps) {
  // Sanitize HTML content
  const sanitizedContent = sanitizeHtml(content, {
    allowedTags: sanitizeHtml.defaults.allowedTags.concat([
      'img', 'figure', 'figcaption', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'iframe'
    ]),
    allowedAttributes: {
      ...sanitizeHtml.defaults.allowedAttributes,
      img: ['src', 'alt', 'class', 'style', 'width', 'height'],
      a: ['href', 'target', 'rel'],
      iframe: ['src', 'width', 'height', 'frameborder', 'allow', 'allowfullscreen'],
      '*': ['class', 'style']
    },
    allowedIframeHostnames: ['www.youtube.com', 'player.vimeo.com'],
    transformTags: {
      'a': sanitizeHtml.simpleTransform('a', { target: '_blank', rel: 'noopener noreferrer' })
    }
  });

  return (
    <>
      <style>{`
        /* Scoped blog post styles */
        .blog-post {
          --heading-color: #0f172a; /* neutral-900 */
          --body-color: #404040; /* neutral-700 */
          --link-color: #d68800; /* brand orange */
          --quote-bg: rgba(214, 136, 0, 0.06);
        }

        /* Heading hierarchy and spacing */
        .blog-post h1,
        .blog-post h2,
        .blog-post h3,
        .blog-post h4,
        .blog-post h5,
        .blog-post h6 {
          margin-top: 2.5rem;
          margin-bottom: 1.25rem;
          line-height: 1.2;
          color: var(--heading-color);
          font-weight: 700;
          font-family: 'Sofia Pro', sans-serif;
        }

        /* Heading sizes */
        .blog-post h1 { font-size: 2.5rem; }
        .blog-post h2 { font-size: 2rem; }
        .blog-post h3 { font-size: 1.5rem; }
        .blog-post h4 { font-size: 1.25rem; }
        .blog-post h5 { font-size: 1.125rem; }
        .blog-post h6 { 
          font-size: 1rem; 
          text-transform: uppercase; 
          letter-spacing: 0.02em; 
        }

        /* WordPress block headings */
        .blog-post h2.wp-block-heading {
          padding-bottom: 0;
          font-size: 2rem;
        }

        /* Body text consistency */
        /* Forced dark text for white background */
        .blog-post p,
        .blog-post li,
        .blog-post .body-copy {
          font-size: 1.25rem; /* text-xl */
          color: var(--body-color);
          line-height: 1.75; /* leading-relaxed */
          margin-bottom: 1.25rem;
          font-family: 'Sofia Pro', sans-serif;
        }

        /* Lists */
        .blog-post ul,
        .blog-post ol {
          margin: 1.5rem 0;
          padding-left: 1.5rem;
        }

        .blog-post ul {
          list-style-type: disc;
        }

        .blog-post ol {
          list-style-type: decimal;
        }

        /* Links */
        .blog-post a {
          color: var(--link-color);
          text-decoration: none;
          transition: color 0.2s;
        }

        .blog-post a:hover {
          text-decoration: underline;
        }

        /* Strong/Bold */
        .blog-post strong {
          font-weight: 600;
          color: #171717; /* neutral-900 */
        }

        /* Image spacing and separation */
        .blog-post img,
        .blog-post figure {
          display: block;
          margin: 1.5rem 0;
        }

        .blog-post figure img {
          width: 100%;
          height: auto;
          border-radius: 4px;
        }

        .blog-post figure figcaption {
          font-size: 0.875rem;
          color: #737373; /* neutral-500 */
          margin-top: 0.5rem;
          text-align: center;
        }

        /* Blockquote with icon */
        .blog-post blockquote {
          position: relative;
          margin: 2rem 0;
          padding: 1.25rem 1.25rem 1.25rem 3.25rem;
          font-size: 1.25rem;
          font-style: italic;
          color: #171717;
          border-left: 4px solid var(--link-color);
          background: var(--quote-bg);
          border-radius: 4px;
        }

        /* Quote icon */
        .blog-post blockquote::before {
          content: "“";
          position: absolute;
          left: 0.75rem;
          top: 0.5rem;
          font-size: 3rem;
          line-height: 1;
          color: var(--link-color);
          font-weight: 700;
        }

        /* Code blocks */
        .blog-post code {
          background: #f3f4f6;
          padding: 0.125rem 0.375rem;
          border-radius: 0.25rem;
          font-size: 0.875rem;
          color: #1f2937;
        }

        .blog-post pre {
          background: #f3f4f6;
          padding: 1rem;
          border-radius: 0.25rem;
          overflow-x: auto;
          margin: 1.5rem 0;
        }

        .blog-post pre code {
          background: transparent;
          padding: 0;
        }
      `}</style>
      <div
        className="blog-post"
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
      />
    </>
  );
}
